import {
  CheckRounded,
  NotificationImportantOutlined,
} from "@mui/icons-material";
import { Box, Typography, useMediaQuery } from "@mui/material";
import {
  amber,
  green,
  grey,
  lightGreen,
  orange,
  red,
} from "@mui/material/colors";
import { isArray, isEmpty, isNil, isObject, size } from "lodash";
import { useMemo } from "react";
import ChipItem from "../../../_src_shared/components/display/ChipItem";
import ExpandCollapseIconButton from "../../buttons/ExpandCollapseIconButton";
import Flx from "../../layout/Flx";
import RequiredDocumentCompletedProgressBar from "../../ui/progressBars/RequiredDocumentCompletedProgressBar";
import UnderwritingRequirementsCompletedProgressBar from "../../ui/progressBars/UnderwritingRequirementsCompletedProgressBar ";
import { useSelector } from "react-redux";

const FinancingRequestHeader = ({
  loanName,
  loanProductType,
  loanPurpose,
  loan,
  open,
  setOpen,

  loanDocuments,
  modalTypeName,
}) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  return (
    <Box
      onClick={() => setOpen(!open)}
      sx={{
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        position: "relative",
        gap: 1,
        m: -2,
        px: 2,
        pt: 2.5,
        pb: 1,
        transition: "background 100ms ease-in-out",

        "&:hover": {
          background: `linear-gradient(
        to bottom,
        ${grey[50]} 0%,
        ${open ? "transparent" : grey[50]} 100%
      )`,
        },
      }}
    >
      <Flx jw jb wrap ai={"flex-start"} g={1}>
        <Flx column g={0.5}>
          <Flx ac g={0.5} wrap>
            <Typography
              variant="h6"
              sx={{
                mr: 1,
                fontSize: isMobile ? ".9em" : "1.2em",
                fontWeight: 700,
                lineHeight: 1.3,
                color: "#0f172a",
                // mb: 0.3,
              }}
            >
              {loanName || "Untitled Loan"}
            </Typography>
            {/* <ActionRequiredChip loanDocuments={loanDocuments} /> */}
          </Flx>
          <Flx ac g={1.5} wrap sx={{ mb: 0.3 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? "0.75em" : "0.85em",
                color: "#64748b",
              }}
            >
              {loanProductType}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? "0.75em" : "0.85em",
                color: "#64748b",
              }}
            >
              •
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? "0.75em" : "0.85em",
                color: "#64748b",
              }}
            >
              {loanPurpose}
            </Typography>
          </Flx>
        </Flx>

        <Flx ac g={1}>
          <PendingLoanTermsChip loan={loan} />
          <ActionRequiredChip loan={loan} />
        </Flx>
      </Flx>

      <Flx ac g={0}>
        <UnderwritingRequirementsCompletedProgressBar
          loan={loan}
          // loanDocuments={loanDocuments}
        />

        <ExpandCollapseIconButton modalTypeName={modalTypeName} active={open} />
      </Flx>
    </Box>
  );
};

const ActionRequiredChip = ({ loan }) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const filteredUnderwritingRequirements = useMemo(() => {
    const uwRequirements = loan?.underwritingRequirements;
    return (
      uwRequirements?.filter(
        (req) =>
          req?.status !== "Accepted" &&
          req?.docStipStatus !== "Received - Pending Review" &&
          req?.docStipStatus !== "Accepted",
      ) || []
    );
  }, [loan]);

  const hasRequirements = !isEmpty(filteredUnderwritingRequirements);

  const chipStyles = useMemo(() => {
    return {
      p: 1,
      borderRadius: 40,
      mb: 2,

      flexShrink: 0,
      ".txt": {
        lineHeight: 1,
        fontWeight: 500,
        pr: 0.5,
        fontSize: isMobile ? "11px" : "13px",
      },
    };
  }, [isMobile]);

  if (!hasRequirements) {
    return;
  }
  return (
    <ChipItem
      color={red[900]}
      sx={chipStyles}
      icon={
        <NotificationImportantOutlined
          sx={{
            mr: 0.4,
            ml: 0.4,
            fontSize: isMobile ? "14px !important" : "16px !important",
          }}
          // className="thin"
        />
      }
      backgroundColor={red[50]}
      value={"Action Required"}
    />
  );
};
const PendingLoanTermsChip = ({ loan }) => {
  const dealId = loan?._id;
  const profile = useSelector((state) => state?.profile);
  const isMobile = useMediaQuery("(max-width:768px)");
  const chipStyles = useMemo(() => {
    return {
      p: 1,
      borderRadius: 40,
      mb: 2,

      flexShrink: 0,
      ".txt": {
        lineHeight: 1,
        fontWeight: 500,
        pr: 0.5,
        fontSize: isMobile ? "11px" : "13px",
      },
    };
  }, [isMobile]);

  const loanPendingTermsSheets = useMemo(() => {
    const profilePendingTermSheets = profile?.pendingTermSheets || [];

    const matchingTermSheets = profilePendingTermSheets.filter(
      (ts) => ts?.dealId === dealId,
    );
    if (isEmpty(matchingTermSheets)) {
      return [];
    }
    return matchingTermSheets;
  }, [profile?.pendingTermSheets, dealId]);

  const loanAcceptedTermsSheets = useMemo(() => {
    const profileAcceptedTermSheets = profile?.acceptedTermSheets || [];
    const matchingTermSheets = profileAcceptedTermSheets.filter(
      (ts) => ts?.dealId === dealId,
    );

    if (isEmpty(matchingTermSheets)) {
      return [];
    }
    return matchingTermSheets;
  }, [profile?.acceptedTermSheets, dealId]);

  const pendingTermSheetsEmpty = isEmpty(loanPendingTermsSheets);
  const acceptedTermSheetsEmpty = isEmpty(loanAcceptedTermsSheets);

  if (pendingTermSheetsEmpty && acceptedTermSheetsEmpty) {
    return;
  }

  if (!pendingTermSheetsEmpty && acceptedTermSheetsEmpty) {
    return (
      <ChipItem
        value={"Pending Term Sheet Review"}
        color={orange[900]}
        backgroundColor={orange[50]}
        sx={chipStyles}
        icon={
          <NotificationImportantOutlined
            sx={{
              mr: 0.4,
              ml: 0.4,
              fontSize: isMobile ? "14px !important" : "16px !important",
            }}
          />
        }
      />
    );
  }

  if (pendingTermSheetsEmpty && !acceptedTermSheetsEmpty) {
    return (
      <ChipItem
        value={"Term Sheet Approved"}
        color={green[900]}
        backgroundColor={green[50]}
        sx={chipStyles}
        icon={
          <CheckRounded
            sx={{
              mr: 0.4,
              ml: 0.4,
              fontSize: isMobile ? "14px !important" : "16px !important",
            }}
          />
        }
      />
    );
  }
};
// const ActionRequiredChip = ({ loan }) => {
//   const text = useMemo(() => {
//     const requiredSize = 8;
//     const { loanDocuments, underwritingRequirements = [] } = loan;

//     if (!isEmpty(underwritingRequirements)) {
//       return ACTION_TYPES.ACTIVE_CONDITIONS;
//     }

//     if (isArray(loanDocuments)) {
//       if (
//         !isEmpty(loanDocuments?.filter((doc) => doc.docType === "Term Sheet"))
//       )
//         return ACTION_TYPES.ACTIVE_CONDITIONS;
//     }

//     return;
//     if (size(loanDocuments) < requiredSize) {
//       return ACTION_TYPES.INCOMPLETE;
//     }

//     if (size(loanDocuments) >= requiredSize) {
//       const filteredAccepted = loanDocuments?.filter(
//         (doc) => doc.documentStatus === "Accepted",
//       );
//       if (size(filteredAccepted) === requiredSize) {
//         return ACTION_TYPES.SUBMITTED_ACCEPTED;
//       }
//       return ACTION_TYPES.SUBMITTED_WAITING_REVIEW;
//     }
//   }, [loan]);

//   const styles = useMemo(() => {
//     let textColor = green[900];
//     let backgroundColor = lightGreen[50];
//     if (text === ACTION_TYPES.SUBMITTED_WAITING_REVIEW) {
//       textColor = "#0f172a";
//       backgroundColor = amber[100];
//     }
//     if (
//       text === ACTION_TYPES.INCOMPLETE ||
//       text === ACTION_TYPES.ACTIVE_CONDITIONS
//     ) {
//       textColor = red[900];
//       backgroundColor = red[50];
//     }
//     if (text === ACTION_TYPES.SUBMITTED_ACCEPTED) {
//       backgroundColor = green[100];
//     }
//     return {
//       color: textColor,
//       backgroundColor: backgroundColor,
//     };
//   }, [text]);

//   if (isNil(text)) {
//     return;
//   }
//   return (
//     <ChipItem
//       color={styles.color}
//       sx={{
//         p: 1,
//         borderRadius: 40,
//         mb: 2,
//         // mb: 0.5,
//         flexShrink: 0,
//         ".txt": {
//           // fontSize: "13px",
//           lineHeight: 1,
//           fontWeight: 500,
//         },
//       }}
//       icon={
//         <NotificationImportantOutlined
//           sx={{ mr: 0.4, ml: 0.4, fontSize: "16px !important" }}
//           // className="thin"
//         />
//       }
//       backgroundColor={styles.backgroundColor}
//       value={text}
//     />
//   );
// };

export default FinancingRequestHeader;
